export const DO_NOT_USE_TIMOCOM_FOR_COMPANY_IDS = new Set([12]);

function safeJsonParse(text) {
	try {
		return JSON.parse(text);
	} catch {
		return undefined;
	}
}

function createMessageId() {
	return `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function createCleanup({ getTimer, setTimer, getWs, setWs }) {
	return () => {
		const timer = getTimer();
		if (timer) {
			clearTimeout(timer);
			setTimer(undefined);
		}
		const ws = getWs();
		if (ws) {
			try {
				ws.close();
			} catch {
				// ignore
			}
			setWs(undefined);
		}
	};
}

function sendCargosCommandToCompanion(type, cargos, options = {}) {
	const url = options.url ?? "ws://127.0.0.1:17654";
	const id = options.id ?? createMessageId();
	const onEvent =
		typeof options.onEvent === "function" ? options.onEvent : null;
	const companionAppConfigEncrypted = options.companionAppConfigEncrypted;

	const expectedResults =
		(cargos?.trans && Array.isArray(cargos.trans) && cargos.trans.length > 0
			? 1
			: 0) +
		(cargos?.timocom &&
		Array.isArray(cargos.timocom) &&
		cargos.timocom.length > 0
			? 1
			: 0);

	return new Promise((resolve, reject) => {
		let settled = false;
		let resultCount = 0;
		const resultPayloads = [];
		let timer;
		let ws;
		const cleanup = createCleanup({
			getTimer: () => timer,
			setTimer: (v) => {
				timer = v;
			},
			getWs: () => ws,
			setWs: (v) => {
				ws = v;
			},
		});

		const fail = (err) => {
			if (settled) return;
			settled = true;
			cleanup();
			reject(err);
		};

		const ok = (payload) => {
			if (settled) return;
			settled = true;
			cleanup();
			resolve(payload);
		};

		try {
			ws = new WebSocket(url);
		} catch (err) {
			fail(
				err instanceof Error ? err : new Error("Failed to create WebSocket"),
			);
			return;
		}

		ws.addEventListener("open", () => {
			try {
				const msg = { id, type, cargos };
				if (typeof companionAppConfigEncrypted === "string") {
					msg.companionAppConfigEncrypted = companionAppConfigEncrypted;
				}
				if (typeof options.useTimocomAi === "boolean") {
					msg.useTimocomAi = options.useTimocomAi;
				}
				if (options?.userCompanyId) {
					msg.userCompanyId = options.userCompanyId;
				}
				ws.send(JSON.stringify(msg));
			} catch (err) {
				fail(err instanceof Error ? err : new Error("Failed to send message"));
			}
		});

		ws.addEventListener("message", (event) => {
			const text = typeof event.data === "string" ? event.data : "";
			const parsed = safeJsonParse(text);
			if (!parsed || typeof parsed !== "object") return;

			const msg = parsed;
			if (msg.id !== id) return;

			if (msg.ok === true) {
				if (msg.type === "event") {
					// streaming progress events; do not settle the promise
					onEvent?.(msg.event);
					return;
				}
				if (msg.type === "result") {
					resultCount += 1;
					resultPayloads.push(msg.result);

					// When both trans + timocom are requested, the companion will send
					// one "result" per platform. Keep the socket open until all are received.
					if (expectedResults <= 1 || resultCount >= expectedResults) {
						if (resultPayloads.length === 1) {
							ok({
								received: true,
								count: resultPayloads[0]?.count ?? 0,
								results: [resultPayloads[0]],
							});
							return;
						}

						const totalCount = resultPayloads.reduce((sum, r) => {
							const c =
								r && typeof r === "object" && "count" in r ? r.count : 0;
							return sum + (typeof c === "number" ? c : 0);
						}, 0);

						ok({
							received: true,
							count: totalCount,
							results: resultPayloads,
						});
						return;
					}

					return;
				}
				ok({
					received: true,
					count: msg?.result?.count ?? 0,
					results: [msg.result],
				});
				return;
			}

			const message = msg?.error?.message || "Companion returned an error";
			fail(new Error(message));
		});

		ws.addEventListener("error", () => {
			fail(new Error("Failed to connect to companion app"));
		});

		ws.addEventListener("close", () => {
			if (!settled) {
				fail(new Error("Companion connection closed"));
			}
		});
	});
}

/**
 * Sends a list of cargos to the desktop companion app over WebSocket.
 * This is best-effort: caller can ignore failures if companion isn't running.
 * cargos should be an object with keys "timocom" and "trans", each containing an array of cargos.
 */
export function sendCargosToCompanion(cargos, options = {}) {
	return sendCargosCommandToCompanion("postCargos", cargos, options);
}

/**
 * Requests the desktop companion app to remove cargos (e.g. unpublish/withdraw) via automation.
 * This is best-effort: caller can ignore failures if companion isn't running.
 */
export function removeCargosViaCompanion(cargos, options = {}) {
	return sendCargosCommandToCompanion("removeCargos", cargos, options);
}

/**
 * Requests the desktop companion app to refresh cargos (e.g. update/renew) via automation.
 * This is best-effort: caller can ignore failures if companion isn't running.
 */
export function refreshCargosViaCompanion(cargos, options = {}) {
	return sendCargosCommandToCompanion("refreshCargos", cargos, options);
}

/**
 * Best-effort check whether the desktop companion app is running.
 * Resolves to true if a WebSocket connection can be opened.
 */
export function healthCheck(options = {}) {
	const url = options.url ?? "ws://127.0.0.1:17654";
	const timeoutMs = options.timeoutMs ?? 750;

	return new Promise((resolve) => {
		let settled = false;
		let opened = false;
		let timer;
		let ws;
		const cleanup = createCleanup({
			getTimer: () => timer,
			setTimer: (v) => {
				timer = v;
			},
			getWs: () => ws,
			setWs: (v) => {
				ws = v;
			},
		});

		const finish = (running) => {
			if (settled) return;
			settled = true;
			cleanup();
			resolve(Boolean(running));
		};

		timer = setTimeout(() => {
			finish(false);
		}, timeoutMs);

		try {
			ws = new WebSocket(url);
		} catch {
			finish(false);
			return;
		}

		ws.addEventListener("open", () => {
			opened = true;
			finish(true);
		});

		ws.addEventListener("error", () => {
			if (!opened) finish(false);
		});

		ws.addEventListener("close", () => {
			if (!opened) finish(false);
		});
	});
}
